import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Target,
  BarChart3,
  Settings,
  Plus,
  Edit,
  Eye,
  Trash2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Users,
  MapPin,
  Clock,
  DollarSign,
  Star,
  Filter,
  Search,
  RefreshCw,
  User,
  Building,
  Phone,
  Mail,
  Globe,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import ScoringRuleModal from './ScoringRuleModal';
import QualificationCriteriaModal from './QualificationCriteriaModal';
import {
  sanitizeTextInput,
  validateNumberInput,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { formatDate } from '../../../utils/stringUtils';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import scoringRuleService from '../../../api/crm/leads/scoringRuleService';
import qualificationCriteriaService from '../../../api/crm/leads/qualificationCriteriaService';
import leadScoringService from '../../../api/crm/leads/leadScoringService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { companySizeService } from '../../../api/crm/config/configService';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { usePermissions } from '../../../hooks/usePermissions';

const LeadScoringQualification = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  // Initial filter values from URL (for persistence across refresh)
  const initialSubTab = searchParams.get('subTab') || 'scoring';
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialSortBy = searchParams.get('sortBy') || 'createdAt';
  const initialSortOrder = searchParams.get('sortOrder') || 'desc';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';

  const [activeTab, setActiveTab] = useState(initialSubTab);
  const isInitialTabMount = useRef(true);

  // Check if user has permission to create, edit, or delete
  const hasCreatePermission = canCreate('lead_management');
  const hasUpdatePermission = canUpdate('lead_management');
  const hasDeletePermission = canDelete('lead_management');
  const [masterCompanySizes, setMasterCompanySizes] = useState([]);

  useEffect(() => {
    const fetchCompanySizes = async () => {
      try {
        const response = await companySizeService.getAll({ isActive: true });
        if (response.success && Array.isArray(response.data)) {
          setMasterCompanySizes(response.data);
        }
      } catch (error) {
      }
    };
    fetchCompanySizes();
  }, []);

  const [showScoringModal, setShowScoringModal] = useState(false);
  const [selectedScoring, setSelectedScoring] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Sorting state
  const [scoringSortConfig, setScoringSortConfig] = useState({
    key: initialSubTab === 'scoring' ? initialSortBy : 'createdAt',
    direction: initialSubTab === 'scoring' ? initialSortOrder : 'desc',
  });
  const [qualificationSortConfig, setQualificationSortConfig] = useState({
    key: initialSubTab === 'qualification' ? initialSortBy : 'createdAt',
    direction: initialSubTab === 'qualification' ? initialSortOrder : 'desc',
  });

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const showNotification = useCallback((message, type = 'success') => {
    if (type === 'error') {
      toast.error(message);
    } else if (type === 'success') {
      toast.success(message);
    } else if (type === 'info') {
      toast(message, { icon: 'ℹ️' });
    } else {
      toast(message);
    }
  }, []);

  // Clear selection and filters when tab changes (skip on initial mount so
  // URL-restored filters are preserved on refresh)
  useEffect(() => {
    if (isInitialTabMount.current) {
      isInitialTabMount.current = false;
      return;
    }
    setSelectedRows(new Set());
    setSearchTerm('');
    setStatusFilter('all');
    setScoringDateRange({ startDate: '', endDate: '' });
    setQualificationDateRange({ startDate: '', endDate: '' });
    setScoringSortConfig({ key: 'createdAt', direction: 'desc' });
    setQualificationSortConfig({ key: 'createdAt', direction: 'desc' });
  }, [activeTab]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Permission-based access control - use granular permissions instead of role checks
  // This allows custom permission assignments beyond just roles

  const [scoringFormData, setScoringFormData] = useState({
    name: '',
    description: '',
    rules: [],
    isActive: true
  });
  const [newRule, setNewRule] = useState({ field: 'source', operator: 'equals', condition: '', score: '' });

  // Field-specific options for scoring rules
  const fieldOptions = {
    source: {
      label: 'Source',
      type: 'enum',
      options: [
        { value: 'website', label: 'Website' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'facebook', label: 'Social Media' },
        { value: 'instagram', label: 'Social Media' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'google_ads', label: 'Google Ads' },
        { value: 'referral', label: 'Referral' },
        { value: 'cold_call', label: 'Cold Call' },
        { value: 'walk_in', label: 'Walk In' },
        { value: 'other', label: 'Manual Entry' }
      ],
      operators: ['equals', 'not_equals']
    },
    region: {
      label: 'Region',
      type: 'enum',
      options: [
        { value: 'North', label: 'North' },
        { value: 'South', label: 'South' },
        { value: 'East', label: 'East' },
        { value: 'West', label: 'West' },
        { value: 'Central', label: 'Central' }
      ],
      operators: ['equals', 'not_equals']
    },
    companySize: {
      label: 'Company Size',
      type: 'enum',
      options: masterCompanySizes.map(size => ({
        value: size._id,
        label: size.name
      })),
      operators: ['equals', 'not_equals']
    },
    budget: {
      label: 'Budget',
      type: 'number',
      operators: ['greater_than', 'less_than', 'equals']
    },
    designation: {
      label: 'Role',
      type: 'enum',
      options: [
        { value: 'CEO/Founder', label: 'CEO/Founder' },
        { value: 'CTO', label: 'CTO' },
        { value: 'CFO', label: 'CFO' },
        { value: 'VP/Director', label: 'VP/Director' },
        { value: 'Manager', label: 'Manager' },
        { value: 'Executive', label: 'Executive' },
        { value: 'Other', label: 'Other' }
      ],
      operators: ['equals', 'not_equals']
    },
    timeline: {
      label: 'Timeline',
      type: 'enum',
      options: [
        { value: 'Immediate (0-30 days)', label: 'Immediate (0-30 days)' },
        { value: 'Short-term (1-3 months)', label: 'Short-term (1-3 months)' },
        { value: 'Medium-term (3-6 months)', label: 'Medium-term (3-6 months)' },
        { value: 'Long-term (6+ months)', label: 'Long-term (6+ months)' }
      ],
      operators: ['equals', 'not_equals']
    }
  };

  // Helper to format operator display
  const formatOperator = (operator) => {
    const labels = {
      'equals': 'Equals',
      'not_equals': 'Not Equals',
      'contains': 'Contains',
      'in': 'In (Multiple)',
      'not_in': 'Not In',
      'greater_than': 'Greater Than',
      'less_than': 'Less Than',
      'greater_than_equal': 'Greater Than or Equal',
      'less_than_equal': 'Less Than or Equal',
      'between': 'Between'
    };
    return labels[operator] || operator?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || operator;
  };

  // Helper to format values (conditions)
  const formatValue = (field, value) => {
    if (!value) return '';
    // Handle special cases or numeric values directly
    if (typeof value === 'number') return String(value);

    // Try to find label in options
    const fieldConfig = fieldOptions[field];
    if (fieldConfig?.options) {
      if (field === 'companySize') {
        const option = fieldConfig.options.find(opt => 
          String(opt.value) === String(value) || 
          String(opt.label).toLowerCase() === String(value).toLowerCase()
        );
        if (option) return option.label;
      } else {
        const option = fieldConfig.options.find(opt => String(opt.value) === String(value));
        if (option) return option.label;
      }
    }

    // Fallback: format snake_case to Title Case
    return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Get current field configuration for scoring rules
  const getCurrentFieldConfig = () => {
    return fieldOptions[newRule.field] || { type: 'text', operators: ['equals', 'contains'] };
  };

  // Get current field configuration for qualification criteria
  const getCurrentCriteriaFieldConfig = () => {
    // Map 'role' to 'designation' for qualification criteria
    const field = newCriterion.field === 'role' ? 'designation' : newCriterion.field;
    return fieldOptions[field] || { type: 'text', operators: ['equals', 'contains'] };
  };

  // Reset condition when field changes
  const handleFieldChange = (field) => {
    const fieldConfig = fieldOptions[field];
    setNewRule({
      field,
      operator: fieldConfig?.operators[0] || 'equals',
      condition: '',
      score: ''
    });
  };

  // Reset criterion when field changes
  const handleCriteriaFieldChange = (field) => {
    // Map 'role' to 'designation' for fieldOptions lookup
    const lookupField = field === 'role' ? 'designation' : field;
    const fieldConfig = fieldOptions[lookupField];
    setNewCriterion({
      field,
      operator: fieldConfig?.operators[0] || 'equals',
      value: ''
    });
  };
  const [isSaving, setIsSaving] = useState(false);
  const [scoringErrors, setScoringErrors] = useState({});
  const [criteriaErrors, setCriteriaErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteCriteriaModal, setShowDeleteCriteriaModal] = useState(false);
  const [criteriaToDelete, setCriteriaToDelete] = useState(null);
  const [isDeletingCriteria, setIsDeletingCriteria] = useState(false);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [criteriaFormData, setCriteriaFormData] = useState({
    name: '',
    description: '',
    criteria: [],
    isActive: true,
    leadTemperature: 'Cold'
  });
  const [newCriterion, setNewCriterion] = useState({ field: 'budget', operator: 'greater_than', value: '' });
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [scoreFilter, setScoreFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // State for scoring rules
  const [scoringRules, setScoringRules] = useState([]);
  const [scoringDateRange, setScoringDateRange] = useState(
    initialSubTab === 'scoring'
      ? { startDate: initialStartDate, endDate: initialEndDate }
      : { startDate: '', endDate: '' }
  );

  // State for qualification criteria
  const [qualificationCriteria, setQualificationCriteria] = useState([]);
  const [qualificationDateRange, setQualificationDateRange] = useState(
    initialSubTab === 'qualification'
      ? { startDate: initialStartDate, endDate: initialEndDate }
      : { startDate: '', endDate: '' }
  );

  // Sync filter/sort state to URL search params for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['subTab', 'search', 'status', 'startDate', 'endDate', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'scoring') params.set('subTab', activeTab);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);

      const activeDateRange = activeTab === 'scoring' ? scoringDateRange : qualificationDateRange;
      if (activeDateRange.startDate) params.set('startDate', activeDateRange.startDate);
      if (activeDateRange.endDate) params.set('endDate', activeDateRange.endDate);

      const activeSort = activeTab === 'scoring' ? scoringSortConfig : qualificationSortConfig;
      if (activeSort.key && activeSort.key !== 'createdAt') params.set('sortBy', activeSort.key);
      if (activeSort.direction && activeSort.direction !== 'desc') params.set('sortOrder', activeSort.direction);

      return params;
    }, { replace: true });
  }, [
    activeTab,
    searchTerm,
    statusFilter,
    scoringDateRange,
    qualificationDateRange,
    scoringSortConfig,
    qualificationSortConfig,
    setSearchParams,
  ]);

  // State for lead scores
  const [leadScores, setLeadScores] = useState([]);
  const [leadScoresPagination, setLeadScoresPagination] = useState({});

  // Load data on component mount and filter changes
  useEffect(() => {
    if (activeTab === 'scoring') {
      const timeoutId = setTimeout(() => {
        loadScoringRules();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [scoringDateRange, searchTerm, statusFilter, activeTab]);

  useEffect(() => {
    if (activeTab === 'qualification') {
      const timeoutId = setTimeout(() => {
        loadQualificationCriteria();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [qualificationDateRange, searchTerm, statusFilter, activeTab]);

  // Load scoring rules
  const loadScoringRules = async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter !== 'all') filters.isActive = statusFilter === 'active';

      const result = await scoringRuleService.getAllScoringRules(filters);
      if (result.success) {
        let rules = result.rules || [];
        if (scoringDateRange.startDate || scoringDateRange.endDate) {
          rules = rules.filter(rule => {
            const ruleDate = new Date(rule.createdAt);
            if (scoringDateRange.startDate && ruleDate < new Date(scoringDateRange.startDate)) return false;
            if (scoringDateRange.endDate) {
              const borderDate = new Date(scoringDateRange.endDate);
              borderDate.setHours(23, 59, 59, 999);
              if (ruleDate > borderDate) return false;
            }
            return true;
          });
        }
        setScoringRules(rules);
      } else {
        showNotification(result.error || 'Failed to load scoring rules', 'error');
      }
    } catch (error) {
      showNotification('Failed to load scoring rules', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Load qualification criteria
  const loadQualificationCriteria = async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (statusFilter !== 'all') filters.isActive = statusFilter === 'active';

      const result = await qualificationCriteriaService.getAllQualificationCriteria(filters);
      if (result.success) {
        let criteria = result.criteria || [];
        if (qualificationDateRange.startDate || qualificationDateRange.endDate) {
          criteria = criteria.filter(item => {
            const criteriaDate = new Date(item.createdAt);
            if (qualificationDateRange.startDate && criteriaDate < new Date(qualificationDateRange.startDate)) return false;
            if (qualificationDateRange.endDate) {
              const borderDate = new Date(qualificationDateRange.endDate);
              borderDate.setHours(23, 59, 59, 999);
              if (criteriaDate > borderDate) return false;
            }
            return true;
          });
        }
        setQualificationCriteria(criteria);
      } else {
        showNotification(result.error || 'Failed to load qualification criteria', 'error');
      }
    } catch (error) {
      showNotification('Failed to load qualification criteria', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Load lead scores
  const loadLeadScores = async (page = 1) => {
    setIsLoading(true);
    try {
      const filters = {
        page,
        limit: 10,
        search: searchTerm || undefined,
        scoreLabel: scoreFilter && scoreFilter !== 'all' ? scoreFilter : undefined
      };
      const result = await leadScoringService.getLeadScores(filters);
      if (result.success) {
        setLeadScores(result.leads || []);
        setLeadScoresPagination(result.pagination || {});
      } else {
        showNotification(result.error || 'Failed to load lead scores', 'error');
      }
    } catch (error) {
      showNotification('Failed to load lead scores', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Sorting functions
  const handleScoringSort = (key) => {
    let direction = 'asc';
    if (scoringSortConfig.key === key && scoringSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setScoringSortConfig({ key, direction });
  };

  const handleQualificationSort = (key) => {
    let direction = 'asc';
    if (qualificationSortConfig.key === key && qualificationSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setQualificationSortConfig({ key, direction });
  };

  const hasActiveFilters = useMemo(() => {
    if (activeTab === 'scoring') {
      return searchTerm.trim() !== '' || statusFilter !== 'all' || scoringDateRange.startDate !== '' || scoringDateRange.endDate !== '';
    } else {
      return searchTerm.trim() !== '' || statusFilter !== 'all' || qualificationDateRange.startDate !== '' || qualificationDateRange.endDate !== '';
    }
  }, [searchTerm, statusFilter, scoringDateRange, qualificationDateRange, activeTab]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    if (activeTab === 'scoring') {
      setScoringDateRange({ startDate: '', endDate: '' });
    } else {
      setQualificationDateRange({ startDate: '', endDate: '' });
    }
  };

  const getSortedData = (data, config) => {
    if (!config.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[config.key];
      let bValue = b[config.key];

      if (config.key === 'rulesCount') {
        aValue = a.rules?.length || 0;
        bValue = b.rules?.length || 0;
      } else if (config.key === 'criteriaCount') {
        aValue = a.criteria?.length || 0;
        bValue = b.criteria?.length || 0;
      } else if (config.key === 'createdAt' || config.key === 'updatedAt') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (typeof aValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      if (aValue < bValue) {
        return config.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return config.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedScoringRules = useMemo(() => {
    return getSortedData(scoringRules, scoringSortConfig);
  }, [scoringRules, scoringSortConfig]);

  const sortedQualificationCriteria = useMemo(() => {
    return getSortedData(qualificationCriteria, qualificationSortConfig);
  }, [qualificationCriteria, qualificationSortConfig]);

  const baseExportFiltersScoring = useMemo(() => {
    const f = {};
    if (searchTerm.trim()) f.search = searchTerm.trim();
    if (statusFilter !== 'all') f.isActive = statusFilter === 'active';
    if (scoringDateRange.startDate) f.startDate = scoringDateRange.startDate;
    if (scoringDateRange.endDate) f.endDate = scoringDateRange.endDate;
    return f;
  }, [searchTerm, statusFilter, scoringDateRange.startDate, scoringDateRange.endDate]);

  const baseExportFiltersQualification = useMemo(() => {
    const f = {};
    if (searchTerm.trim()) f.search = searchTerm.trim();
    if (statusFilter !== 'all') f.isActive = statusFilter === 'active';
    if (qualificationDateRange.startDate) f.startDate = qualificationDateRange.startDate;
    if (qualificationDateRange.endDate) f.endDate = qualificationDateRange.endDate;
    return f;
  }, [searchTerm, statusFilter, qualificationDateRange.startDate, qualificationDateRange.endDate]);

  const currentPageScoringRuleIds = useMemo(
    () => sortedScoringRules.map((r) => String(r._id || r.id || '')).filter(Boolean),
    [sortedScoringRules]
  );

  const currentPageCriteriaIds = useMemo(
    () => sortedQualificationCriteria.map((c) => String(c._id || c.id || '')).filter(Boolean),
    [sortedQualificationCriteria]
  );

  // Sort Header Component
  const SortableHeader = ({ label, sortKey, currentSort, onSort }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {currentSort.key === sortKey ? (
          currentSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
        ) : (
          <ArrowUpDown className="h-4 w-4 text-gray-400" />
        )}
      </div>
    </th>
  );


  // Handle row selection
  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Handle select all
  const handleSelectAll = (e) => {
    const data = activeTab === 'scoring' ? sortedScoringRules : sortedQualificationCriteria;
    if (e.target.checked) {
      const allIds = data.map(item => item._id || item.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const requestBackendExport = useCallback(
    async ({
      module,
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overrideSortBy = null,
      overrideSortOrder = null,
      currentPageIds: overrideCurrentPageIds = null,
      overrideLimit = null,
    }) => {
      const isScoring = module === 'crm-scoring-rules';
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? (isScoring ? currentPageScoringRuleIds : currentPageCriteriaIds))
          : [];
      const sortConfig = isScoring ? scoringSortConfig : qualificationSortConfig;
      const listLen = isScoring ? sortedScoringRules.length : sortedQualificationCriteria.length;

      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module,
            format,
            scope,
            filters: overrideFilters ?? (isScoring ? baseExportFiltersScoring : baseExportFiltersQualification),
            page: 1,
            limit: overrideLimit ?? Math.max(listLen, 1),
            sortBy: overrideSortBy ?? sortConfig.key,
            sortOrder: overrideSortOrder ?? sortConfig.direction,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `${module}-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFiltersScoring,
      baseExportFiltersQualification,
      currentPageScoringRuleIds,
      currentPageCriteriaIds,
      scoringSortConfig,
      qualificationSortConfig,
      sortedScoringRules.length,
      sortedQualificationCriteria.length,
    ]
  );

  const handleScoringQualificationSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const module = activeTab === 'scoring' ? 'crm-scoring-rules' : 'crm-qualification-criteria';
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        showNotification('Nothing selected to export.', 'error');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          module,
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showNotification(`${ids.length} row(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        showNotification(error?.message || 'Export failed.', 'error');
      } finally {
        setExportLoading(false);
      }
    },
    [activeTab, selectedRows, requestBackendExport, showNotification]
  );

  const handleScoringQualificationScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const module = activeTab === 'scoring' ? 'crm-scoring-rules' : 'crm-qualification-criteria';
      const isScoring = activeTab === 'scoring';
      const listLen = isScoring ? sortedScoringRules.length : sortedQualificationCriteria.length;
      const pageIds = isScoring ? currentPageScoringRuleIds : currentPageCriteriaIds;

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageIds.length) {
          showNotification('No data on current page to export.', 'error');
          return;
        }

        if (scope === 'filtered' && listLen === 0) {
          showNotification('No items match the current filters.', 'error');
          return;
        }

        const baseFilters = isScoring ? baseExportFiltersScoring : baseExportFiltersQualification;

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            showNotification('Please select both start and end dates.', 'error');
            return;
          }
          await requestBackendExport({
            module,
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            module,
            scope: 'all',
            format,
            overrideFilters: {},
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            module,
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            module,
            scope: 'currentPage',
            format,
            currentPageIds: pageIds,
            overrideLimit: Math.max(listLen, 1),
          });
        } else {
          return;
        }

        showNotification('Export started.');
        setShowExportModal(false);
      } catch (error) {
        showNotification(error?.message || 'Export failed.', 'error');
      } finally {
        setExportLoading(false);
      }
    },
    [
      activeTab,
      baseExportFiltersScoring,
      baseExportFiltersQualification,
      currentPageScoringRuleIds,
      currentPageCriteriaIds,
      sortedScoringRules.length,
      sortedQualificationCriteria.length,
      requestBackendExport,
      showNotification,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Reload lead scores when filters change
  // (Lead Scores tab removed — filtering/reload logic disabled)

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    if (score >= 40) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getQualificationColor = (qualification) => {
    switch (qualification) {
      case 'Highly Qualified': return 'bg-green-100 text-green-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      case 'Disqualified': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper functions
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  const formatLeadDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const mapSourceToFrontend = (source) => {
    const sourceMap = {
      'website': 'Website',
      'facebook': 'Social Media',
      'instagram': 'Social Media',
      'linkedin': 'LinkedIn',
      'google_ads': 'Google Ads',
      'whatsapp': 'WhatsApp',
      'referral': 'Referral',
      'cold_call': 'Cold Call',
      'walk_in': 'Walk In',
      'manual_entry': 'Manual Entry',
      'other': 'Other'
    };
    return sourceMap[source] || source || 'Manual Entry';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Contacted': return 'bg-green-100 text-green-800';
      case 'Qualified': return 'bg-purple-100 text-purple-800';
      case 'Disqualified': return 'bg-red-100 text-red-800';
      case 'Closed Won': return 'bg-emerald-100 text-emerald-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateScoring = () => {
    setSelectedScoring(null);
    setScoringFormData({
      name: '',
      description: '',
      rules: [],
      isActive: true
    });
    setScoringErrors({});
    setNewRule({ field: 'source', operator: 'equals', condition: '', score: '' });
    setShowScoringModal(true);
  };

  const handleEditScoring = (scoring) => {
    setSelectedScoring(scoring);
    // Transform rules from backend format to frontend format
    const transformedRules = scoring.rules.map(rule => ({
      field: rule.field,
      operator: rule.operator || 'equals',
      condition: rule.value,
      score: rule.score
    }));
    setScoringFormData({
      name: scoring.name,
      description: scoring.description,
      rules: transformedRules,
      isActive: scoring.isActive
    });
    setScoringErrors({});
    setNewRule({ field: 'source', operator: 'equals', condition: '', score: '' });
    setShowScoringModal(true);
  };

  const handleDeleteScoring = (scoringId) => {
    const scoring = scoringRules.find(s => s._id === scoringId || s.id === scoringId);
    if (scoring) {
      setRuleToDelete(scoring);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteScoring = async () => {
    if (!ruleToDelete) return;
    setIsDeleting(true);
    setIsLoading(true);
    try {
      const result = await scoringRuleService.deleteScoringRule(ruleToDelete._id || ruleToDelete.id);
      if (result.success) {
        await loadScoringRules();
        showNotification(`Scoring rule deleted successfully!`, 'success');
        setShowDeleteModal(false);
        setRuleToDelete(null);
      } else {
        showNotification(result.error || 'Failed to delete scoring rule', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete scoring rule', 'error');
    } finally {
      setIsDeleting(false);
      setIsLoading(false);
    }
  };

  const handleToggleScoring = async (scoringId) => {
    const scoring = scoringRules.find(s => s._id === scoringId || s.id === scoringId);
    if (scoring) {
      setIsLoading(true);
      try {
        const result = await scoringRuleService.updateScoringRule(scoringId, {
          isActive: !scoring.isActive
        });
        if (result.success) {
          await loadScoringRules();
          showNotification(`Scoring rule ${scoring.isActive ? 'deactivated' : 'activated'} successfully!`, 'success');
        } else {
          showNotification(result.error || 'Failed to update scoring rule', 'error');
        }
      } catch (error) {
        showNotification('Failed to update scoring rule', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleViewScoring = (scoring) => {
    navigate(`/scoring_rule/${scoring._id || scoring.id}`);
  };

  const handleViewCriteria = (criteria) => {
    navigate(`/qualification_criteria/${criteria._id || criteria.id}`);
  };

  // COMMENTED OUT: Recalculate Scores feature temporarily disabled
  // const handleRecalculateScores = async () => {
  //   setIsLoading(true);
  //   showNotification('Lead scores recalculation process started...', 'info');

  //   try {
  //     const result = await leadScoringService.recalculateAllLeadScores();
  //     if (result.success) {
  //       await loadLeadScores(currentPage);
  //       showNotification(`Lead scores recalculated successfully! Processed ${result.data?.processed || 0} leads.`, 'success');
  //     } else {
  //       showNotification(result.error || 'Failed to recalculate lead scores', 'error');
  //     }
  //   } catch (error) {
  //     showNotification('Failed to recalculate lead scores', 'error');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleExportScores = () => {
    // Create CSV content for lead scores
    const csvContent = leadScores.map(lead =>
      `${lead.name},${lead.email},${lead.company},${lead.score},${lead.qualification}`
    ).join('\n');

    const blob = new Blob([`Name,Email,Company,Score,Qualification\n${csvContent}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead_scores_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleEditQualificationCriteria = (criteriaId) => {
    const criteria = qualificationCriteria.find(c => c._id === criteriaId || c.id === criteriaId);
    if (criteria) {
      setSelectedCriteria(criteria);
      // Map 'designation' back to 'role' for frontend display
      const mappedCriteria = criteria.criteria.map(c => ({
        ...c,
        field: c.field === 'designation' ? 'role' : c.field
      }));
      // Ensure isActive is always a boolean
      const isActiveValue = criteria.isActive;
      const isActiveBoolean = isActiveValue === true || isActiveValue === 'true';

      setCriteriaFormData({
        name: criteria.name,
        description: criteria.description,
        criteria: mappedCriteria,
        isActive: isActiveBoolean,
        qualificationLogic: criteria.qualificationLogic || 'all',
        qualificationResult: 'qualified',
        leadTemperature: criteria.leadTemperature || 'Cold'
      });
      setCriteriaErrors({});
      setNewCriterion({ field: 'budget', operator: 'greater_than', value: '' });
      setShowCriteriaModal(true);
    }
  };

  const handleDeleteQualificationCriteria = (criteriaId) => {
    const criteria = qualificationCriteria.find(c => c._id === criteriaId || c.id === criteriaId);
    if (criteria) {
      setCriteriaToDelete(criteria);
      setShowDeleteCriteriaModal(true);
    }
  };

  const confirmDeleteQualificationCriteria = async () => {
    if (!criteriaToDelete) return;
    setIsDeletingCriteria(true);
    setIsLoading(true);
    try {
      const result = await qualificationCriteriaService.deleteQualificationCriteria(criteriaToDelete._id || criteriaToDelete.id);
      if (result.success) {
        await loadQualificationCriteria();
        showNotification(`Qualification criteria deleted successfully!`, 'success');
        setShowDeleteCriteriaModal(false);
        setCriteriaToDelete(null);
      } else {
        showNotification(result.error || 'Failed to delete qualification criteria', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete qualification criteria', 'error');
    } finally {
      setIsDeletingCriteria(false);
      setIsLoading(false);
    }
  };

  const handleToggleQualificationCriteria = async (criteriaId) => {
    const criteria = qualificationCriteria.find(c => c._id === criteriaId || c.id === criteriaId);
    if (criteria) {
      setIsLoading(true);
      try {
        const result = await qualificationCriteriaService.updateQualificationCriteria(criteriaId, {
          isActive: !criteria.isActive
        });
        if (result.success) {
          await loadQualificationCriteria();
          showNotification(`Qualification criteria ${criteria.isActive ? 'deactivated' : 'activated'} successfully!`, 'success');
        } else {
          showNotification(result.error || 'Failed to update qualification criteria', 'error');
        }
      } catch (error) {
        showNotification('Failed to update qualification criteria', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCreateCriteria = () => {
    setSelectedCriteria(null);
    setCriteriaFormData({
      name: '',
      description: '',
      criteria: [],
      isActive: true,
      leadTemperature: 'Cold'
    });
    setCriteriaErrors({});
    setNewCriterion({ field: 'budget', operator: 'greater_than', value: '' });
    setShowCriteriaModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    setIsLoading(true);
    try {
      const selectedIds = Array.from(selectedRows);
      let successCount = 0;
      let lastError = null;

      for (const id of selectedIds) {
        let result;
        if (activeTab === 'scoring') {
          result = await scoringRuleService.deleteScoringRule(id);
        } else {
          result = await qualificationCriteriaService.deleteQualificationCriteria(id);
        }

        if (result.success) {
          successCount++;
        } else {
          lastError = result.error;
        }
      }

      if (successCount > 0) {
        if (activeTab === 'scoring') {
          await loadScoringRules();
        } else {
          await loadQualificationCriteria();
        }
        showNotification(`Successfully deleted ${successCount} ${activeTab === 'scoring' ? 'scoring rules' : 'qualification criteria'}!`, 'success');
        setSelectedRows(new Set());
      }

      if (successCount < selectedIds.length) {
        showNotification(lastError || 'Some items could not be deleted', 'error');
      }
      setShowBulkDeleteModal(false);
    } catch (error) {
      showNotification('An error occurred during bulk deletion', 'error');
    } finally {
      setIsBulkDeleting(false);
      setIsLoading(false);
    }
  };

  // Handle form data changes
  const handleFormDataChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;

    // Handle boolean values (for checkboxes like isActive)
    if (typeof value === 'boolean') {
      processedValue = value;
    } else if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'textarea') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    }

    setScoringFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
    // Note: Errors are only cleared on form submit, not while typing (HRMS pattern)
  };

  // Validate scoring form (HRMS pattern)
  const validateScoringForm = () => {
    const newErrors = {};

    // Validate name
    if (!scoringFormData.name.trim()) {
      newErrors.name = 'Rule name is required.';
    } else if (scoringFormData.name.trim().length > 100) {
      newErrors.name = 'Rule name cannot exceed 100 characters.';
    }

    // Validate description
    if (scoringFormData.description && scoringFormData.description.trim().length > 1000) {
      newErrors.description = 'Description cannot exceed 1000 characters.';
    }

    // Validate rules array
    if (scoringFormData.rules.length === 0) {
      newErrors.rules = 'Please add at least one scoring rule.';
    }

    setScoringErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle criteria form data changes
  const handleCriteriaFormDataChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;

    // Handle boolean values (for checkboxes like isActive)
    if (typeof value === 'boolean') {
      processedValue = value;
    } else if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'textarea') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    }

    setCriteriaFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
    // Note: Errors are only cleared on form submit, not while typing (HRMS pattern)
  };

  // Validate criteria form (HRMS pattern)
  const validateCriteriaForm = () => {
    const newErrors = {};

    // Validate name
    if (!criteriaFormData.name.trim()) {
      newErrors.name = 'Criteria name is required.';
    } else if (criteriaFormData.name.trim().length > 100) {
      newErrors.name = 'Criteria name cannot exceed 100 characters.';
    }

    // Validate description
    if (criteriaFormData.description && criteriaFormData.description.trim().length > 1000) {
      newErrors.description = 'Description cannot exceed 1000 characters.';
    }

    // Validate criteria array
    if (criteriaFormData.criteria.length === 0) {
      newErrors.criteria = 'Please add at least one qualification criterion.';
    }

    setCriteriaErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle adding new criterion
  const handleAddCriterion = () => {
    // Reset specific new criterion error
    setCriteriaErrors(prev => ({ ...prev, newCriterion: undefined }));

    if (newCriterion.value) {
      // Map 'role' to 'designation' for backend compatibility
      const criterionToAdd = {
        ...newCriterion,
        field: newCriterion.field === 'role' ? 'designation' : newCriterion.field
      };
      // Remove label from criterion object
      delete criterionToAdd.label;
      setCriteriaFormData(prev => ({
        ...prev,
        criteria: [...prev.criteria, criterionToAdd]
      }));

      // Clear specific new rule error and generic criteria error
      setCriteriaErrors(prev => ({
        ...prev,
        criteria: undefined,
        newCriterion: undefined
      }));

      // Reset with default operator for budget field
      const budgetConfig = fieldOptions.budget;
      setNewCriterion({
        field: 'budget',
        operator: budgetConfig?.operators[0] || 'greater_than',
        value: ''
      });
    } else {
      setCriteriaErrors(prev => ({
        ...prev,
        newCriterion: 'Please select a value.'
      }));
    }
  };

  // Handle removing criterion
  const handleRemoveCriterion = (index) => {
    setCriteriaFormData(prev => ({
      ...prev,
      criteria: prev.criteria.filter((_, i) => i !== index)
    }));
  };

  // Handle saving criteria
  const handleSaveCriteria = async () => {
    // Validate form using HRMS pattern
    if (!validateCriteriaForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Map 'role' to 'designation' for backend compatibility
      // Always set qualificationResult to 'qualified' as per requirements
      // Remove label from criteria as it's no longer needed
      // Ensure isActive is always a boolean (not empty string or undefined)
      // If checkbox is unchecked, isActive should be false
      const isActiveValue = criteriaFormData.isActive;
      const isActiveBoolean = isActiveValue === true ||
        isActiveValue === 'true' ||
        (typeof isActiveValue === 'string' && isActiveValue.toLowerCase() === 'true');

      const criteriaToSave = {
        ...criteriaFormData,
        qualificationResult: 'qualified',
        isActive: isActiveBoolean,
        criteria: criteriaFormData.criteria.map(c => {
          const criterion = {
            ...c,
            field: c.field === 'role' ? 'designation' : c.field
          };
          // Remove label if it exists
          delete criterion.label;
          return criterion;
        })
      };

      if (selectedCriteria) {
        // Update existing criteria
        const result = await qualificationCriteriaService.updateQualificationCriteria(
          selectedCriteria._id || selectedCriteria.id,
          criteriaToSave
        );
        if (result.success) {
          await loadQualificationCriteria();
          showNotification('Qualification criteria updated successfully!', 'success');
          setCriteriaFormData({
            name: '',
            description: '',
            criteria: [],
            isActive: true,
            qualificationLogic: 'all',
            qualificationResult: 'qualified'
          });
          setSelectedCriteria(null);
          setShowCriteriaModal(false);
        } else {
          showNotification(result.error || 'Failed to update qualification criteria', 'error');
        }
      } else {
        // Create new criteria
        const result = await qualificationCriteriaService.createQualificationCriteria(criteriaToSave);
        if (result.success) {
          await loadQualificationCriteria();
          showNotification('Qualification criteria created successfully!', 'success');
          setCriteriaFormData({
            name: '',
            description: '',
            criteria: [],
            isActive: true,
            qualificationLogic: 'all',
            qualificationResult: 'qualified'
          });
          setSelectedCriteria(null);
          setShowCriteriaModal(false);
        } else {
          showNotification(result.error || 'Failed to create qualification criteria', 'error');
        }
      }
    } catch (error) {
      showNotification('Failed to save qualification criteria', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle adding new rule
  const handleAddRule = () => {
    const scoreValue = Number(newRule.score);
    const currentTotalScore = scoringFormData.rules.reduce((sum, rule) => sum + Number(rule.score), 0);

    // Reset specific new rule error
    setScoringErrors(prev => ({ ...prev, newRule: undefined }));

    if (!newRule.condition) {
      setScoringErrors(prev => ({ ...prev, newRule: 'Please select a condition.' }));
      return;
    }

    if (!scoreValue || scoreValue <= 0 || scoreValue > 100) {
      setScoringErrors(prev => ({ ...prev, newRule: 'Please enter a valid score (1-100).' }));
      return;
    }

    if (currentTotalScore + scoreValue > 100) {
      setScoringErrors(prev => ({
        ...prev,
        newRule: `Total score cannot exceed 100. Current total: ${currentTotalScore}, Remaining: ${100 - currentTotalScore}`
      }));
      return;
    }

    setScoringFormData(prev => ({
      ...prev,
      rules: [...prev.rules, {
        field: newRule.field,
        operator: newRule.operator || 'equals',
        condition: newRule.condition,
        score: scoreValue
      }]
    }));

    // Clear global rules error if it existed
    setScoringErrors(prev => ({
      ...prev,
      rules: undefined,
      newRule: undefined
    }));

    setNewRule({ field: 'source', operator: 'equals', condition: '', score: '' });
  };

  // Handle removing rule
  const handleRemoveRule = (index) => {
    setScoringFormData(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  // Handle saving scoring rule
  const handleSaveScoring = async () => {
    // Validate form using HRMS pattern
    if (!validateScoringForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Transform rules to match backend format
      const rulesToSave = scoringFormData.rules.map(rule => ({
        field: rule.field,
        operator: rule.operator || 'equals',
        value: rule.condition || rule.value,
        score: rule.score
      }));

      const ruleData = {
        ...scoringFormData,
        rules: rulesToSave
      };

      if (selectedScoring) {
        // Update existing rule
        const result = await scoringRuleService.updateScoringRule(
          selectedScoring._id || selectedScoring.id,
          ruleData
        );
        if (result.success) {
          await loadScoringRules();
          showNotification('Scoring rule updated successfully!', 'success');
          setScoringFormData({
            name: '',
            description: '',
            rules: [],
            isActive: true
          });
          setSelectedScoring(null);
          setShowScoringModal(false);
        } else {
          showNotification(result.error || 'Failed to update scoring rule', 'error');
        }
      } else {
        // Create new rule
        const result = await scoringRuleService.createScoringRule(ruleData);
        if (result.success) {
          await loadScoringRules();
          showNotification('Scoring rule created successfully!', 'success');
          setScoringFormData({
            name: '',
            description: '',
            rules: [],
            isActive: true
          });
          setSelectedScoring(null);
          setShowScoringModal(false);
        } else {
          showNotification(result.error || 'Failed to create scoring rule', 'error');
        }
      }
    } catch (error) {
      showNotification('Failed to save scoring rule', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lead Scoring & Qualification</h1>
            <p className="text-gray-600">Configure scoring algorithms and qualification criteria for leads</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected`
                    : 'Export'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleScoringQualificationSelectedFormatExport}
              />
            </div>
            {selectedRows.size > 0 && hasDeletePermission && (
              <Button
                variant="outline"
                icon={isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {isBulkDeleting ? 'Deleting...' : `Delete (${selectedRows.size})`}
              </Button>
            )}
            {hasCreatePermission && (
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={activeTab === 'scoring' ? handleCreateScoring : handleCreateCriteria}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                {activeTab === 'scoring' ? 'Create Scoring Rule' : 'Create Criteria'}
              </Button>
            )}

          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { key: 'scoring', label: 'Scoring Rules', count: scoringRules.length },
                { key: 'qualification', label: 'Qualification Criteria', count: qualificationCriteria.length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                    ? 'border-agrosaf-green text-agrosaf-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${activeTab === tab.key
                    ? 'bg-agrosaf-green text-white'
                    : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'scoring' && (
              <div className="space-y-6">
                <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 p-1">
                      <DateRangeFilter
                        startDate={scoringDateRange.startDate}
                        endDate={scoringDateRange.endDate}
                        onChange={setScoringDateRange}
                      />
                    </div>
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        icon={<X className="h-4 w-4" />}
                        onClick={handleClearFilters}
                        className="flex-shrink-0 whitespace-nowrap"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left w-12">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                            checked={sortedScoringRules.length > 0 && selectedRows.size === sortedScoringRules.length}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <SortableHeader label="Name" sortKey="name" currentSort={scoringSortConfig} onSort={handleScoringSort} />
                        <SortableHeader label="Rules" sortKey="rulesCount" currentSort={scoringSortConfig} onSort={handleScoringSort} />
                        <SortableHeader label="Created" sortKey="createdAt" currentSort={scoringSortConfig} onSort={handleScoringSort} />
                        <SortableHeader label="Modified" sortKey="updatedAt" currentSort={scoringSortConfig} onSort={handleScoringSort} />
                        <SortableHeader label="Status" sortKey="isActive" currentSort={scoringSortConfig} onSort={handleScoringSort} />
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedScoringRules.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                            No scoring rules found.
                          </td>
                        </tr>
                      ) : (
                        sortedScoringRules.map((rule) => {
                          const ruleId = rule._id || rule.id;
                          return (
                            <tr key={ruleId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                                  checked={selectedRows.has(ruleId)}
                                  onChange={() => handleSelectRow(ruleId)}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap max-w-[220px]">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate capitalize">{rule.name}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => navigate(`/scoring_rule/${rule._id || rule.id}`)}
                                  className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                  title="View rule details"
                                >
                                  {rule.rules && rule.rules.length ? rule.rules.length : 0}
                                </button>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{formatDate(rule.createdAt, { includeTime: false })}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{new Date(rule.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {rule.updatedAt ? (
                                  <>
                                    <div className="text-sm text-gray-900">{formatDate(rule.updatedAt, { includeTime: false })}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{new Date(rule.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                                  </>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {rule.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewScoring(rule)}
                                    className="text-green-600 hover:text-green-900"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {hasUpdatePermission && (
                                    <button
                                      onClick={() => handleEditScoring(rule)}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Edit Rule"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  )}
                                  {hasUpdatePermission && (
                                    <button
                                      onClick={() => handleToggleScoring(rule._id || rule.id)}
                                      className={rule.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                                      title={rule.isActive ? 'Deactivate Rule' : 'Activate Rule'}
                                    >
                                      {rule.isActive ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                    </button>
                                  )}
                                  {hasDeletePermission && (
                                    <button
                                      onClick={() => handleDeleteScoring(rule._id || rule.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete Rule"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal for Qualification Criteria */}
            {showDeleteCriteriaModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Delete Qualification Criteria</h3>
                    <button
                      onClick={() => {
                        setShowDeleteCriteriaModal(false);
                        setCriteriaToDelete(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isDeletingCriteria}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-600 break-words">
                      Are you sure you want to delete the qualification criteria: <span className="font-medium text-gray-900 first-letter:capitalize">"{criteriaToDelete?.name}"</span>? This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteCriteriaModal(false);
                        setCriteriaToDelete(null);
                      }}
                      disabled={isDeletingCriteria}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      icon={isDeletingCriteria ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      className="bg-red-600 hover:bg-red-700"
                      onClick={confirmDeleteQualificationCriteria}
                      disabled={isDeletingCriteria}
                    >
                      {isDeletingCriteria ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal for Scoring Rules */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Delete Scoring Rule</h3>
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        setRuleToDelete(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isDeleting}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-600 break-words">
                      Are you sure you want to delete the scoring rule: <span className="font-medium text-gray-900 first-letter:capitalize">"{ruleToDelete?.name}"</span>? This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setRuleToDelete(null);
                      }}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                    {hasDeletePermission && (
                      <Button
                        variant="primary"
                        icon={isDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        className="bg-red-600 hover:bg-red-700"
                        onClick={confirmDeleteScoring}
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      Bulk Delete {activeTab === 'scoring' ? 'Scoring Rules' : 'Qualification Criteria'}
                    </h3>
                    <button
                      onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={isBulkDeleting}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected {activeTab === 'scoring' ? 'scoring rules' : 'qualification criteria'}? This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowBulkDeleteModal(false)}
                      disabled={isBulkDeleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      icon={isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      className="bg-red-600 hover:bg-red-700"
                      onClick={confirmBulkDelete}
                      disabled={isBulkDeleting}
                    >
                      {isBulkDeleting ? 'Deleting...' : `Delete All Selected (${selectedRows.size})`}
                    </Button>
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'qualification' && (
              <div className="space-y-6">
                <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 p-1">
                      <DateRangeFilter
                        startDate={qualificationDateRange.startDate}
                        endDate={qualificationDateRange.endDate}
                        onChange={setQualificationDateRange}
                      />
                    </div>
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        icon={<X className="h-4 w-4" />}
                        onClick={handleClearFilters}
                        className="flex-shrink-0 whitespace-nowrap"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left w-12">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                            checked={sortedQualificationCriteria.length > 0 && selectedRows.size === sortedQualificationCriteria.length}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <SortableHeader label="Name" sortKey="name" currentSort={qualificationSortConfig} onSort={handleQualificationSort} />
                        <SortableHeader label="Rules" sortKey="criteriaCount" currentSort={qualificationSortConfig} onSort={handleQualificationSort} />
                        <SortableHeader label="Created" sortKey="createdAt" currentSort={qualificationSortConfig} onSort={handleQualificationSort} />
                        <SortableHeader label="Modified" sortKey="updatedAt" currentSort={qualificationSortConfig} onSort={handleQualificationSort} />
                        <SortableHeader label="Status" sortKey="isActive" currentSort={qualificationSortConfig} onSort={handleQualificationSort} />
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedQualificationCriteria.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                            No qualification criteria found.
                          </td>
                        </tr>
                      ) : (
                        sortedQualificationCriteria.map((criteria) => {
                          const criteriaId = criteria._id || criteria.id;
                          return (
                            <tr key={criteriaId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                                  checked={selectedRows.has(criteriaId)}
                                  onChange={() => handleSelectRow(criteriaId)}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap max-w-[220px]">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate capitalize">{criteria.name}</div>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleViewCriteria(criteria)}
                                  className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                  title="View criteria details"
                                >
                                  {criteria.criteria && criteria.criteria.length ? criteria.criteria.length : 0}
                                </button>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{formatDate(criteria.createdAt, { includeTime: false })}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{new Date(criteria.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {criteria.updatedAt ? (
                                  <>
                                    <div className="text-sm text-gray-900">{formatDate(criteria.updatedAt, { includeTime: false })}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{new Date(criteria.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                                  </>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${criteria.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {criteria.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewCriteria(criteria)}
                                    className="text-green-600 hover:text-green-900"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {hasUpdatePermission && (
                                    <button
                                      onClick={() => handleEditQualificationCriteria(criteria._id || criteria.id)}
                                      className="text-blue-600 hover:text-blue-900"
                                      title="Edit Criteria"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  )}
                                  {hasUpdatePermission && (
                                    <button
                                      onClick={() => handleToggleQualificationCriteria(criteria._id || criteria.id)}
                                      className={criteria.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                                      title={criteria.isActive ? 'Deactivate Criteria' : 'Activate Criteria'}
                                    >
                                      {criteria.isActive ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                    </button>
                                  )}
                                  {hasDeletePermission && (
                                    <button
                                      onClick={() => handleDeleteQualificationCriteria(criteria._id || criteria.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete Criteria"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


          </div>
        </div>

        {/* Scoring Modal */}
        {showScoringModal && (
          <ScoringRuleModal
            isOpen={showScoringModal}
            onClose={() => {
              setShowScoringModal(false);
              setScoringErrors({});
            }}
            selectedScoring={selectedScoring}
            onSave={handleSaveScoring}
            isSaving={isSaving}
            scoringFormData={scoringFormData}
            setScoringFormData={setScoringFormData}
            scoringErrors={scoringErrors}
            setScoringErrors={setScoringErrors}
          />
        )}

        {/* Criteria Modal */}
        {showCriteriaModal && (
          <QualificationCriteriaModal
            isOpen={showCriteriaModal}
            onClose={() => {
              setShowCriteriaModal(false);
              setCriteriaErrors({});
            }}
            selectedCriteria={selectedCriteria}
            onSave={handleSaveCriteria}
            isSaving={isSaving}
            criteriaFormData={criteriaFormData}
            setCriteriaFormData={setCriteriaFormData}
            criteriaErrors={criteriaErrors}
            setCriteriaErrors={setCriteriaErrors}
          />
        )}

        {/* Lead Detail Modal */}
        {showLeadModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Lead Details</h3>
                <button
                  onClick={() => setShowLeadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Lead Header */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-agrosaf-green rounded-full flex items-center justify-center">
                        <User className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 capitalize">
                          {selectedLead.firstName} {selectedLead.lastName}
                        </h4>
                        <p className="text-gray-600 capitalize">{selectedLead.company}</p>
                        <p className="text-sm text-gray-500">{selectedLead.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getScoreColor(selectedLead.score || 0)}`}>
                        Score: {selectedLead.score || 0}
                      </div>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize mt-2 ${getQualificationColor(selectedLead.isQualified ? 'Qualified' : 'Unqualified')}`}>
                        {selectedLead.isQualified ? 'Qualified' : 'Disqualified'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Building className="h-5 w-5 text-gray-400" />
                      <h5 className="font-medium text-gray-900">Company Information</h5>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Company:</span> <span className="capitalize">{selectedLead.company || '-'}</span></p>
                      <p><span className="text-gray-500">Size:</span> <span className="capitalize">{selectedLead.companySize?.name || selectedLead.companySize || (selectedLead.employeeCount ? `${selectedLead.employeeCount} employees` : '-')}</span></p>
                      <p><span className="text-gray-500">Region:</span> <span className="capitalize">{selectedLead.region || '-'}</span></p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Target className="h-5 w-5 text-gray-400" />
                      <h5 className="font-medium text-gray-900">Lead Details</h5>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Source:</span> <span className="capitalize">{selectedLead.source ? selectedLead.source.charAt(0).toUpperCase() + selectedLead.source.slice(1) : '-'}</span></p>
                      <p><span className="text-gray-500">Role:</span> <span className="capitalize">{selectedLead.designation || '-'}</span></p>
                      <p><span className="text-gray-500">Timeline:</span> <span className="capitalize">{selectedLead.timeline || '-'}</span></p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                      <h5 className="font-medium text-gray-900">Financial Information</h5>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Budget:</span> {selectedLead.budget ? `₹${formatIndianAmount(selectedLead.budget)}` : '-'}</p>
                      <p><span className="text-gray-500">Score:</span> {selectedLead.score || 0}/100</p>
                      <p><span className="text-gray-500">Status:</span> <span className="capitalize">{selectedLead.isQualified ? 'Qualified' : 'Disqualified'}</span></p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h5 className="font-medium text-gray-900 mb-4">Contact Information</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email</p>
                        <p className="text-sm text-gray-500">{selectedLead.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Phone</p>
                        <p className="text-sm text-gray-500">{selectedLead.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scoring Breakdown */}
                {selectedLead.scoreBreakdown && selectedLead.scoreBreakdown.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h5 className="font-medium text-gray-900 mb-4">Scoring Breakdown</h5>
                    <div className="space-y-3">
                      {selectedLead.scoreBreakdown.map((breakdown, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 capitalize">
                            {breakdown.ruleName} - {breakdown.field} {breakdown.condition}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            +{breakdown.score} points
                          </span>
                        </div>
                      ))}
                      <div className="border-t pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-900">Total Score</span>
                          <span className="text-lg font-bold text-gray-900">{selectedLead.score || 0}/100</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Last Updated */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Last Scored: {selectedLead.updatedAt ? new Date(selectedLead.updatedAt).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowLeadModal(false)}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  onClick={() => {
                    setShowLeadModal(false);
                    navigate(`/lead_detail_360/${selectedLead._id || selectedLead.id}`);
                  }}
                >
                  View Full Details
                </Button>
              </div>
            </div>
          </div>
        )}

        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          loading={exportLoading}
          showDateRange={true}
          title={activeTab === 'scoring' ? 'Export Scoring Rules' : 'Export Qualification Criteria'}
          scopedFormatExport={{
            onExport: handleScoringQualificationScopedExport,
          }}
        />
      </div >
    </>
  );
};

export default LeadScoringQualification;
